import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';
import { useTheme } from '../contexts/ThemeContext';
import leagueApiService from '../services/leagueApi';

function Navbar() {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [leagues, setLeagues] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const closeTimerRef = useRef(null);

  const getUserDisplayName = () => {
    if (!user) return '';
    const name = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.email?.split('@')[0] || user.username || 'User';
  };

  useEffect(() => {
    if (!user) {
      setLeagues([]);
      return;
    }
    const userId = user.userId || user.id || user.sub;
    if (!userId) return;
    let cancelled = false;
    setLoadingLeagues(true);
    leagueApiService.getLeagues(userId)
      .then((result) => {
        if (cancelled || !result?.success || !result.leagues) return;
        const transformed = result.leagues.map((league) => {
          const settings = typeof league.settings === 'string' ? JSON.parse(league.settings) : league.settings;
          const season = typeof league.season === 'string' ? JSON.parse(league.season) : league.season;
          return {
            id: league.leagueId,
            leagueId: league.leagueId,
            name: league.name,
            seasonYear: season?.year || 2026,
          };
        });
        setLeagues(transformed);
      })
      .catch(() => { if (!cancelled) setLeagues([]); })
      .finally(() => { if (!cancelled) setLoadingLeagues(false); });
    return () => { cancelled = true; };
  }, [user]);

  const handleMyLeaguesMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMyLeaguesMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setDropdownOpen(false), 150);
  };

  const leagueId = (league) => league.leagueId || league.id;

  const displayName = getUserDisplayName();
  const initials = (() => {
    const parts = displayName.replace(/[^A-Za-z'\- ]/g, '').split(/\s+/).filter(Boolean);
    const a = (parts[0] || '')[0] || '';
    const b = (parts[parts.length - 1] || '')[0] || '';
    return ((a + (parts.length > 1 ? b : '')) || displayName[0] || 'U').toUpperCase();
  })();

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-brand" aria-label="TJ's Fantasy Baseball — Home">
          <img src="/logo.svg" alt="TJFB" className="navbar-brand-mark" />
        </Link>

        <div className="navbar-actions">
          <button
            type="button"
            className="navbar-theme-toggle"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggleTheme}
          >
            {isDark ? 'Light' : 'Dark'}
          </button>

          {user ? (
            <>
              <div
                className="navbar-dropdown"
                onMouseEnter={handleMyLeaguesMouseEnter}
                onMouseLeave={handleMyLeaguesMouseLeave}
              >
                <button
                  type="button"
                  className="navbar-link navbar-link-button"
                  aria-haspopup="menu"
                  aria-expanded={dropdownOpen}
                  onClick={() => setDropdownOpen((open) => !open)}
                >
                  My Leagues
                </button>
                {dropdownOpen && (
                  <div className="navbar-dropdown-menu">
                    {loadingLeagues ? (
                      <div className="navbar-dropdown-link" style={{ color: 'var(--muted)' }}>Loading…</div>
                    ) : leagues.length === 0 ? (
                      <div className="navbar-dropdown-link" style={{ color: 'var(--muted)' }}>No leagues yet</div>
                    ) : (
                      leagues.map((league) => (
                        <Link
                          key={leagueId(league)}
                          to={`/league/${leagueId(league)}/roster`}
                          className="navbar-dropdown-link"
                          onClick={() => setDropdownOpen(false)}
                        >
                          {league.name}
                          {league.seasonYear ? (
                            <span style={{ color: 'var(--muted)', fontSize: '13px', marginLeft: '6px' }}>
                              {league.seasonYear}
                            </span>
                          ) : null}
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>

              <Link to="/account" className="navbar-avatar" title={displayName} aria-label={`Account — ${displayName}`}>
                {initials}
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className="navbar-link">Sign In</Link>
              <Link to="/register" className="btn btn-primary btn-sm">
                Get Started
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
